import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const shakingCauses = [
    {
        id: 'high-speeds',
        title: '1. Shaking at High Speeds',
        text: 'If your car feels smooth at low speeds but starts vibrating as you go faster, the issue is often wheel- or tire-related. Common causes include unbalanced wheels, uneven tire wear, bent rims, or tire damage such as bulges or flat spots.'
    },
    {
        id: 'braking',
        title: '2. Shaking While Braking',
        text: 'If the steering wheel or car shakes when you press the brake pedal, the braking system is the likely source. Common causes include warped brake rotors, worn brake pads, or uneven rotor surfaces.'
    },
    {
        id: 'acceleration',
        title: '3. Shaking During Acceleration',
        text: 'If the vibration shows up when you accelerate, especially under load, it may point to drivetrain or engine issues. Common causes include worn engine mounts, faulty spark plugs, fuel system issues, or transmission problems.'
    },
    {
        id: 'idle',
        title: '4. Shaking at Idle or Low Speeds',
        text: 'If your car shakes while stopped or moving slowly, the issue is often related to engine stability. Common causes include dirty or failing spark plugs, air intake or vacuum issues, or worn engine mounts.'
    }
];

const faqs = [
    {
        question: 'Is it safe to drive with a shaking car?',
        answer: 'It depends on the severity. Minor vibration may not be urgent, but strong or worsening shaking should be checked immediately as it can affect safety and lead to costlier repairs.'
    },
    {
        question: 'Can bad tires cause shaking?',
        answer: 'Yes. Unbalanced or damaged tires are one of the most common causes of vibration, especially at higher speeds.'
    },
    {
        question: 'Why does my steering wheel shake when braking?',
        answer: 'This is often due to warped brake rotors or uneven brake pad material transfer, which creates a pulsing or vibration through the steering system.'
    },
    {
        question: 'Can an engine problem cause vibration?',
        answer: 'Yes. Issues like engine misfires (bad spark plugs/coils) or worn engine mounts can create noticeable shaking, especially under load or at idle.'
    }
];

const Blog13 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 13;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5 z-50"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            Why Is My Car Shaking? Common Causes and How to Fix It
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            If your car starts shaking, vibrating, or pulling to one side, it is usually a sign that something in the tires, brakes, suspension, or engine needs attention.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated April 2026 &nbsp;|&nbsp; 8 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/13_1.jpeg"
                            alt="Driver checking tire condition after noticing car vibration"
                            title="Why Is My Car Shaking While Driving?"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: tire_vibration_check.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#why-it-matters" className="text-gray-900 no-underline hover:underline">Why it matters</a></li>
                            <li><a href="#high-speeds" className="text-gray-900 no-underline hover:underline">Shaking at high speeds</a></li>
                            <li><a href="#braking" className="text-gray-900 no-underline hover:underline">Shaking while braking</a></li>
                            <li><a href="#acceleration" className="text-gray-900 no-underline hover:underline">Shaking during acceleration</a></li>
                            <li><a href="#idle" className="text-gray-900 no-underline hover:underline">Shaking at idle</a></li>
                            <li><a href="#checklist" className="text-gray-900 no-underline hover:underline">Inspection checklist</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            The most important clue is not just that the car shakes, but when it shakes. A vibration at highway speed points in a different direction than a vibration under braking, during acceleration, or while sitting still.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Paying attention to that pattern makes diagnosis faster and helps avoid replacing parts based on guesswork. Catching the issue early makes repairs more manageable.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>Why is my car shaking?</strong><br />
                            A car can shake due to issues with tires, brakes, engine components, or suspension. The exact cause depends on whether the vibration happens at high speeds, while braking, during acceleration, or at idle.
                        </p>
                    </section>

                    <section id="why-it-matters" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Why It Matters</h2>
                        <p className="mb-4 text-gray-700">
                            Ignored vibrations often start small before turning into bigger mechanical problems like uneven tire wear, reduced grip, or increased stress on steering parts.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Steering wheel shake while braking often points toward rotor problems. Catching these early is key to car safety.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/13_2.jpeg"
                            alt="Dashboard brake warning area and brake-related indicators"
                            title="Brake-related shaking clues"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: brake_surface_check.png
                        </figcaption>
                    </figure>

                    {shakingCauses.map((cause) => (
                        <section key={cause.id} id={cause.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {cause.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{cause.text}</p>
                        </section>
                    ))}

                    <section id="checklist" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Simple Inspection Checklist
                        </h2>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Inspect tires for visible damage or uneven wear</li>
                            <li>Check tire pressure against the door-jamb sticker</li>
                            <li>Note when the shaking occurs: speed, braking, or idle</li>
                            <li>Listen for unusual sounds: clicking, grinding, or humming</li>
                            <li>Check for dashboard warning lights</li>
                        </ul>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                How CarFido Assistant Helps
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                Diagnosing a shaking car can feel confusing. CarFido Assistant simplifies the process based on your car's specific symptoms.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido Assistant helps you:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Identify likely causes based on when the shaking occurs</li>
                                <li>Follow a guided inspection checklist to check components step by step</li>
                                <li>Recommend next steps in a practical work plan</li>
                                <li>Log symptoms and monitor patterns in your vehicle history</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            A shaking car is rarely random; it is usually an early warning sign. The sooner you pay attention to the pattern, the easier it becomes to pinpoint the cause.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Much like routine maintenance, understanding vibrations early can save time, money, and stress while keeping your car smoother and safer on the road.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#why-it-matters" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Why it matters</a></li>
                            <li><a href="#high-speeds" className="no-underline text-gray-600 hover:text-gray-900 text-sm">High speeds</a></li>
                            <li><a href="#braking" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Braking</a></li>
                            <li><a href="#checklist" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Checklist</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog13;
